import { Tooltip } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { FcCalendar, FcViewDetails } from 'react-icons/fc';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { AssistanceUtilities } from '../../AssistanceUtilities';
import { useAuth } from '../../context/AuthContext';
import { useAvailableModulesContext } from '../../context/AvailableModulesContext';
import Toast from '../../helpers/Toast';
import { Utilities } from '../../helpers/Utilities';
import { AssistanceListDTO } from '../../models/Assistance/AssistanceListDTO';
import { CreateAssistanceDTO } from '../../models/Assistance/CreateAssistanceDTO';
import { GetAssistanceFromEquipmentDTO } from '../../models/Equipment/GetAssistancesFromEquipmentDTO';
import { useNavigateToScheduleCreate } from '../../ScheduleUtilities';
import { AssistanceService } from '../../services/AssistanceService';
import TableComponent from '../TableComponent';

type AssistancesFromEquipmentProps = {
    EquipmentErp: string,
}

function AssistancesFromEquipment(props: AssistancesFromEquipmentProps) {

    const assistanceService = new AssistanceService();
    const navigate = useNavigate();
    const navigateToScheduleCreate = useNavigateToScheduleCreate();

    const [assistances, setAssistances] = useState<AssistanceListDTO[]>([]);
    const pageSizes: number[] = [5, 15, 30];
    const [pageSize, setPageSize] = useState<number>(pageSizes[0]);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [goToPage, setGoToPage] = useState<number>();
    const [pageCount, setPageCount] = useState(1);
    const [isButtonCreateLoading, setIsButtonCreateLoading] = useState<boolean>(false);

    const { currentUser } = useAuth();

    const { availableModules } = useAvailableModulesContext();
    const [isActiveSchedulerModule, setIsActiveSchedulerModule] = useState<boolean>(false);

    useEffect(() => {
        if (availableModules.includes("IsActiveScheduler")) {
            setIsActiveSchedulerModule(true);
        }
    }, [availableModules]);

    const columns = React.useMemo(
        () => [
            {
                Header: "Assistência",
                Cell: (props: any) => {
                    return Utilities.FormattedAssistanceDocument(props.cell.row.original.docType, props.cell.row.original.serie, props.cell.row.original.number);
                },
                width: 12,
            },
            {
                Header: "Técnico",
                accessor: "technician",
                width: 12,
            },
            {
                Header: "Data",
                accessor: "dateString",
                width: 12,
            },
            {
                Header: "Estado",
                accessor: "status",
                width: 12,
            },
            {
                Header: "",
                accessor: " ",
                Cell: (props: any) => {
                    const isValidAssistanceStatusForScheduling = AssistanceUtilities.IsValidAssistanceStatusForScheduling(props.cell.row.original.status);
                    return (
                        <div style={{ display: "flex" }}>
                            <Tooltip
                                title="Ver Detalhes"
                                aria-label="Ver Detalhes"
                                enterNextDelay={500}
                                enterDelay={500}>
                                <div
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        alignContent: "center",
                                        flexDirection: "column",
                                        cursor: "pointer"
                                    }}
                                    onClick={() => {
                                        // Verificamos se cada campo 'relacionado' existe antes de passar para a função
                                        const projectErpId = props.cell.row.original.project?.erpId || null;
                                        const equipmentErpId = props.cell.row.original.equipment?.erpId || null;
                                        const equipmentNumSerie = props.cell.row.original.equipment?.serialNumber || null;
                                        const equipmentBrand = props.cell.row.original.equipment?.brand || null;
                                        const equipmentModel = props.cell.row.original.equipment?.model || null;
                                        const clientErpId = props.cell.row.original.client?.erpId || null;
                                        const number = props.cell.row.original.number;
                                        const docType = props.cell.row.original.docType;
                                        const serie = props.cell.row.original.serie;

                                        createAssistance(projectErpId, equipmentErpId, equipmentNumSerie, equipmentBrand, equipmentModel, clientErpId, number, docType, serie);
                                    }}
                                >
                                    <FcViewDetails className="icons" />
                                </div>
                            </Tooltip>

                            {isActiveSchedulerModule && isValidAssistanceStatusForScheduling &&
                                <Tooltip title="Agendar" aria-label="Agendar" enterNextDelay={500} enterDelay={500}>
                                    <div
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            alignContent: "center",
                                            flexDirection: "column",
                                            cursor: "pointer",
                                        }}
                                        onClick={() => navigateToScheduleCreate(
                                            props.cell.row.original.assistanceId,
                                            props.cell.row.original.docType,
                                            props.cell.row.original.serie,
                                            props.cell.row.original.number,
                                            props.cell.row.original.client,
                                            props.cell.row.original.equipment
                                        )}
                                    >
                                        <FcCalendar className="icons" />
                                    </div>
                                </Tooltip>
                            }
                        </div>
                    );
                },
                width: 5,
            },

        ],
        [isButtonCreateLoading, isActiveSchedulerModule],
    );

    const createAssistance = async (projectErpId: string, equipmentErpId: string, equipmentNumSerie: string, equipmentBrand: string, equipmentModel: string, clientErpId: string, numDoc: number, typeDoc: string, serie: string) => {
        var sendData: CreateAssistanceDTO = {
            equipmentErpId: equipmentErpId,
            equipmentNumSerie: equipmentNumSerie,
            equipmentBrand: equipmentBrand,
            equipmentModel: equipmentModel,
            projectErpId: projectErpId,
            clientErpId: clientErpId,
            numDoc: numDoc,
            typeDoc: typeDoc,
            serie: serie,
            id: 0,
            date: new Date(),
            description: null,
            notes: null,
            clientId: null,
            counter1: 0,
            counter2: 0,
            counter3: 0,
            assistanceType: null,
            stockEntryDocumentErpId: null,
            budgetSellingPrice: null,
            budgetCostPrice: null
        }
        setIsButtonCreateLoading(true);
        var response = await assistanceService.Create(sendData);
        setIsButtonCreateLoading(false);

        if (response.success === false) {
            Toast.Show("error", response.message);
            return;
        }

        // mandar para rota workshop ou backoffice, dependendo das roles e vista preferida
        if (currentUser?.roles?.includes("Técnico Oficina") && currentUser.preferedView == "Oficina") {
            navigate(`/Workshop/Assistance/${response.obj}`);
        }
        else {
            navigate(`/Backoffice/Assistance/${response.obj}`);
        }
    }

    const GetAssistances = async (page: number, pageSize: number) => {
        setIsLoading(true);

        const data: GetAssistanceFromEquipmentDTO = {
            EquipmentErp: String(props.EquipmentErp),
            CurrentPage: currentPage,
            PageSize: pageSize,
        }

        var response = await assistanceService.GetAssistancesFromEquipment(data);

        if (response.success === false) {
            Toast.Show("error", response.message);
            setIsLoading(false);
            return;
        }

        setAssistances(response.items);
        setIsLoading(false);
        setPageCount(response.totalPages);
        setPageSize(pageSize);
        setCurrentPage(page);
        setGoToPage(0)
    }

    useEffect(() => {
        if (props.EquipmentErp !== "") {
            GetAssistances(currentPage ?? 1, pageSize ?? 5)
        }
    }, [props.EquipmentErp, currentPage, pageSize])

    return (
        <div>
            <label style={{ margin: "0px", padding: "13px" }}><b><FormattedMessage id="Screens.EquipmentCreate.Assistances" /></b></label>
            <TableComponent
                columns={columns}
                data={assistances}
                goToPage={goToPage}
                initialPage={currentPage}
                pageCount={pageCount}
                initialPageSize={pageSize}
                pageSizes={pageSizes}
                manualPagination={true}
                loading={isLoading}
                fetchData={GetAssistances}
                initialSortBy={[]}
                disableSortBy={true}
            />
        </div>

    )
}

export default AssistancesFromEquipment